import React, { type ReactNode, Fragment } from 'react';
import {
  AutocompleteFormik,
  CheckboxFormik,
  CheckboxMultipleFormik,
  DatePickerFormik,
  DateTimePickerFormik,
  PharmIndexFormik,
  RadioFormik, RadioGroupFormik,
  SelectFormik,
  TextFieldFormik,
  TimePickerFormik
} from "../index";
import { useField } from "formik";
import { CircularProgress, Tooltip } from "@mui/material";
import { useAutoSaveFieldFormik } from "hooks/useAutoSaveFieldFormik";
import { map } from "lodash";

export function Cell({ children, data, max = 22 }: { children: ReactNode, data: any, max?: number }) {
  return <React.Fragment>
    {(data && data?.length > max) ? (
      <Tooltip title={data} arrow disableInteractive>
        <span style={{ width: '100%' }}>{children}</span>
      </Tooltip>
    ) : (
      <>{children}</>
    )}
  </React.Fragment>
}

const TableGridFormField: React.FC<any> = (props) => {
  const [field] = useField(props.name);

  const { loading } = useAutoSaveFieldFormik({
    name: props.name,
    label: props.label,
    dataId: props.dataId,
    rowId: props.rowId,
    disabled: props.disabled || props.status,
    saveRow: props.options?.saveRow,
    reloadData: props.reloadData,
  });

  const commonProps = {
    name: props.name,
    label: props.title || props.label,
    status: props.status,
    disabled: props.disabled || props.status,
    fullWidth: true,
    loading: loading,
  };

  const renderInput = () => {
    switch (props.type) {
      case 'autocomplete':
        return <AutocompleteFormik {...commonProps} options={props.options} />
      case 'number':
      case 'text': {
        return (
          <TextFieldFormik
            {...commonProps}
            type={props.type}
            InputProps={{
              endAdornment: (
                <Fragment>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                </Fragment>
              ),
            }}
          />
        );
      }
      case 'choice':
      case 'select':
        return props.options?.pharmindex === 'true' ? (
          <PharmIndexFormik {...commonProps} value={field.value} />
        ) : (
          <SelectFormik
            {...commonProps}
            placeholder={props.options?.placeholder}
            helperText={props.options?.help}
            options={props.options.choices}
          />
        );
      case 'date': {
        return (
          <DatePickerFormik
            {...commonProps}
            placeholder={props.options?.placeholder}
            format={props.options.format}
          />
        );
      }
      case 'time': {
        return (
          <TimePickerFormik
            {...commonProps}
            placeholder={props.options?.placeholder}
            format={props.options?.format}
          />
        );
      }
      case 'datetime': {
        return (
          <DateTimePickerFormik
            {...commonProps}
            placeholder={props.options?.placeholder}
            // format={props.options?.format || undefined}
          />
        );
      }
      case 'radio':
        return (
          <RadioGroupFormik
            name={props.name}
            status={props.status}
            options={props.options}
            required={props?.required}
            row={false}
          />
        );
      case 'checkbox':
        return <CheckboxFormik {...commonProps} />;
      case 'checkbox_multiple':
        return (
          <CheckboxMultipleFormik
            id={props.name || ''}
            options={props.options}
            status={props.status}
          />
        );
      default:
        return null;
    }
  }

  return <Cell data={field.value}>{renderInput()}</Cell>;
}

export default TableGridFormField;
